/**
 * Zod schema generation from EntityForm field configuration.
 *
 * Dynamically builds a Zod object schema based on the field definitions,
 * the current visibility map and the current (condition-evaluated) required
 * map. Hidden fields are excluded from validation so that invisible required
 * fields do not block submission.
 *
 * Missing values are handled in ONE place, `buildFieldSchema`, for every
 * field type; the per-type builders only describe a value that is present.
 * A required field with no value fails with its required message -- never
 * zod's raw "Invalid input: expected string, received undefined" -- and an
 * optional field with no value is valid.
 */

import { z } from 'zod';

import type { FieldConfig, FieldType, FieldValidationConfig } from '../types.ts';
import { validationRegistry } from './registry.ts';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the numeric `value` and optional `message` from a min/max config
 * that can be either a plain number or an object `{ value, message? }`.
 */
function unpackMinMax(
  config: number | { value: number; message?: string },
): { value: number; message: string | undefined } {
  if (typeof config === 'number') {
    return { value: config, message: undefined };
  }
  return { value: config.value, message: config.message };
}

/**
 * Extract the pattern string and optional message from a pattern config.
 */
function unpackPattern(
  config: string | { value: string; message?: string },
): { value: string; message: string | undefined } {
  if (typeof config === 'string') {
    return { value: config, message: undefined };
  }
  return { value: config.value, message: config.message };
}

/**
 * Resolve the required error message from the config.
 * - `true` yields a generic default message.
 * - A string is used as-is (typically an i18n key).
 */
function requiredMessage(required: boolean | string | undefined): string {
  if (typeof required === 'string') {
    return required;
  }
  return 'This field is required';
}

/**
 * Whether a validation config marks the field as required: `true`, or a
 * string (the custom required message).
 */
function isRequired(validation: FieldValidationConfig | undefined): boolean {
  return validation?.required === true || typeof validation?.required === 'string';
}

/**
 * A value that was never provided: `undefined` (an untouched field -- the
 * values store leaves it absent) or `null` (a cleared date / time / number
 * input, or a null loaded from the API).
 */
function isMissing(value: unknown): boolean {
  return value === undefined || value === null;
}

/**
 * Guard a REQUIRED field's value schema so that a field with no value fails
 * with the configured required message instead of zod's raw type error.
 *
 * "No value" is a missing value or the empty string -- what an untouched and
 * a cleared field hold -- so both show the same message, whatever the type's
 * own checks (email / url format, pattern, min length) would say about `''`.
 *
 * The pipe stops at the guard's issue, so the value schema only ever sees a
 * present value: its own required rule still rejects a present-but-empty
 * container (`[]`, an unchecked checkbox, a half-filled date range), and a
 * present value of the wrong type still gets the type error.
 */
function requireValue(schema: z.ZodType, message: string): z.ZodType {
  return z
    .unknown()
    .refine((value) => !isMissing(value) && value !== '', message)
    .pipe(schema);
}

// ---------------------------------------------------------------------------
// String-based field schema
// ---------------------------------------------------------------------------

/**
 * Create the base Zod string schema for the given field type.
 *
 * In Zod v4 `z.email()` and `z.url()` are standalone constructors that
 * replace the deprecated `.email()` / `.url()` chain methods.
 */
function baseStringSchema(fieldType: FieldType): z.ZodType {
  if (fieldType === 'email') {
    return z.email();
  }
  if (fieldType === 'url') {
    return z.url();
  }
  return z.string();
}

/**
 * Apply min/max, pattern, and custom refinement constraints to a
 * string-based schema.
 */
function applyStringConstraints(
  schema: z.ZodType,
  validation: FieldValidationConfig,
): z.ZodType {
  let s = schema;

  if (validation.min !== undefined) {
    const { value, message } = unpackMinMax(validation.min);
    s = (s as z.ZodString).min(value, message);
  }

  if (validation.max !== undefined) {
    const { value, message } = unpackMinMax(validation.max);
    s = (s as z.ZodString).max(value, message);
  }

  if (validation.pattern !== undefined) {
    const { value, message } = unpackPattern(validation.pattern);
    if (value.length <= 200) {
      try {
        s = (s as z.ZodString).regex(new RegExp(value), message);
      } catch {
        // Invalid regex pattern in configuration – skip silently
      }
    }
  }

  if (validation.custom !== undefined) {
    const entry = validationRegistry.getRefinement(validation.custom);
    if (entry) {
      s = s.refine(entry.fn, entry.message);
    }
  }

  return s;
}

/**
 * Build a Zod schema for a field whose base type is `z.string()`.
 *
 * Handles: text, email, password, tel, url, textarea, select, radio.
 * "Required" (non-empty) is enforced by `requireValue`, which rejects `''`.
 */
function buildStringSchema(
  fieldType: FieldType,
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  let schema: z.ZodType = baseStringSchema(fieldType);

  if (validation) {
    schema = applyStringConstraints(schema, validation);
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Number field schema
// ---------------------------------------------------------------------------

function buildNumberSchema(
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  // Coerce so that numeric strings (e.g. loaded from the API) validate. A blank
  // string never gets here: `buildFieldSchema` treats it as a missing value.
  let schema: z.ZodType = z.coerce.number();

  if (validation) {
    if (validation.min !== undefined) {
      const { value, message } = unpackMinMax(validation.min);
      schema = (schema as z.ZodNumber).min(value, message);
    }

    if (validation.max !== undefined) {
      const { value, message } = unpackMinMax(validation.max);
      schema = (schema as z.ZodNumber).max(value, message);
    }

    if (validation.custom !== undefined) {
      const entry = validationRegistry.getRefinement(validation.custom);
      if (entry) {
        schema = schema.refine(entry.fn, entry.message);
      }
    }
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Array-based field schema (multiselect, checkboxGroup, file)
// ---------------------------------------------------------------------------

function buildArraySchema(
  fieldType: FieldType,
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  // File arrays hold opaque File objects; the rest hold strings.
  const elementSchema = fieldType === 'file' ? z.any() : z.string();
  let schema: z.ZodType = z.array(elementSchema);

  if (validation) {
    if (validation.min !== undefined) {
      const { value, message } = unpackMinMax(validation.min);
      schema = (schema as z.ZodArray).min(value, message);
    }

    if (validation.max !== undefined) {
      const { value, message } = unpackMinMax(validation.max);
      schema = (schema as z.ZodArray).max(value, message);
    }

    if (validation.custom !== undefined) {
      const entry = validationRegistry.getRefinement(validation.custom);
      if (entry) {
        schema = schema.refine(entry.fn, entry.message);
      }
    }
  }

  if (isRequired(validation)) {
    // "Required" for arrays means at least one item.
    schema = (schema as z.ZodArray).min(
      1,
      requiredMessage(validation?.required),
    );
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Boolean field schema (checkbox)
// ---------------------------------------------------------------------------

function buildBooleanSchema(
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  let schema: z.ZodType = z.boolean();

  if (isRequired(validation)) {
    // "Required" for a checkbox means it must be checked (true).
    schema = schema.refine(
      (v: unknown) => v === true,
      requiredMessage(validation?.required),
    );
  }

  if (validation?.custom !== undefined) {
    const entry = validationRegistry.getRefinement(validation.custom);
    if (entry) {
      schema = schema.refine(entry.fn, entry.message);
    }
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Date field schema
// ---------------------------------------------------------------------------

/**
 * Build a Zod schema for the `date` field type. A cleared picker holds `null`,
 * which `buildFieldSchema` treats as a missing value (required message when
 * required, valid otherwise).
 */
function buildDateSchema(
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  let schema: z.ZodType = z.date();

  if (validation?.custom !== undefined) {
    const entry = validationRegistry.getRefinement(validation.custom);
    if (entry) {
      schema = schema.refine(entry.fn, entry.message);
    }
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Time field schema
// ---------------------------------------------------------------------------

/**
 * Matches a valid `HH:mm` (24h) time string: hours 00-23, minutes 00-59.
 * Mirrors the base TimePicker's contract (canonical `HH:mm`).
 */
const TIME_HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Build a Zod schema for the `time` field type.
 *
 * Time values are plain strings (`HH:mm` or empty) -- unlike `date`, there is
 * no Date conversion, so this is a string schema. A non-empty value must match
 * the `HH:mm` 24h shape; `''` passes the shape check. A CLEARED field (the base
 * TimePicker emits `onChange(null)` on clear) is a missing value for
 * `buildFieldSchema`, like a cleared `date`: an optional time field accepts
 * `null` and `''`, a required one rejects both with its required message.
 */
function buildTimeSchema(
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  let schema: z.ZodType = z
    .string()
    .refine((v) => v === '' || TIME_HHMM_REGEX.test(v), 'Invalid time');

  if (validation?.custom !== undefined) {
    const entry = validationRegistry.getRefinement(validation.custom);
    if (entry) {
      schema = schema.refine(entry.fn, entry.message);
    }
  }

  return schema;
}

// ---------------------------------------------------------------------------
// DateRange field schema
// ---------------------------------------------------------------------------

function buildDateRangeSchema(
  validation: FieldValidationConfig | undefined,
): z.ZodType {
  let schema: z.ZodType = z.object({
    start: z.date().nullable(),
    end: z.date().nullable(),
  });

  if (isRequired(validation)) {
    schema = schema.refine(
      (v: unknown) => {
        const range = v as { start: unknown; end: unknown };
        return range.start !== null && range.end !== null;
      },
      requiredMessage(validation?.required),
    );
  }

  if (validation?.custom !== undefined) {
    const entry = validationRegistry.getRefinement(validation.custom);
    if (entry) {
      schema = schema.refine(entry.fn, entry.message);
    }
  }

  return schema;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Field type categories for routing to the correct schema builder. */
const STRING_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'text',
  'email',
  'password',
  'tel',
  'url',
  'textarea',
  'select',
  'radio',
]);

const ARRAY_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'multiselect',
  'checkboxGroup',
  'file',
]);

/**
 * Route a field to the schema of its type. That schema validates a PRESENT
 * value only; missing values are handled by `buildFieldSchema`.
 */
function buildValueSchema(fieldConfig: FieldConfig): z.ZodType {
  const { type, validation } = fieldConfig;

  if (STRING_TYPES.has(type)) {
    return buildStringSchema(type, validation);
  }

  if (type === 'number') {
    return buildNumberSchema(validation);
  }

  if (ARRAY_TYPES.has(type)) {
    return buildArraySchema(type, validation);
  }

  if (type === 'checkbox') {
    return buildBooleanSchema(validation);
  }

  if (type === 'date') {
    return buildDateSchema(validation);
  }

  if (type === 'time') {
    return buildTimeSchema(validation);
  }

  if (type === 'dateRange') {
    return buildDateRangeSchema(validation);
  }

  // Fallback: accept anything
  return z.any();
}

/**
 * Build the Zod schema for a single field based on its configuration.
 *
 * Missing values are decided here, identically for every field type. A field
 * has no value when it holds `undefined` (untouched), `null` (a cleared date /
 * time / number, or a null loaded from the API) or `''` (a cleared text input,
 * an empty select / radio, or a `''` loaded from the API):
 * - required: no value fails with the required message
 *   (`validation.required` when it is a string, else the default one);
 * - optional: no value is valid -- the type's own checks (email / url format,
 *   pattern, min length, custom refinement) only ever see a present value;
 * - a `number` field also treats a whitespace-only string as no value (never
 *   as 0). For the other types whitespace is a present value.
 *
 * Only validation is affected: the submitted payload keeps the raw value.
 */
export function buildFieldSchema(fieldConfig: FieldConfig): z.ZodType {
  const schema = buildValueSchema(fieldConfig);
  const { validation } = fieldConfig;

  const fieldSchema = isRequired(validation)
    ? requireValue(schema, requiredMessage(validation?.required))
    : schema.nullish();

  // `''` is mapped to `undefined` before either branch, so a cleared optional
  // email / url / pattern / min-length field is valid instead of failing its
  // format check. A blank number (whitespace too) is mapped as well:
  // `z.coerce.number()` would otherwise read it as 0 -- valid for an optional
  // field, or failing its `min` with a bogus message.
  return z.preprocess(
    fieldConfig.type === 'number' ? blankToMissing : emptyToMissing,
    fieldSchema,
  );
}

/**
 * Map the empty string to `undefined`. A whitespace-only string is kept: in a
 * text field it is a value, checked like any other (and a required field
 * treats it as filled).
 */
function emptyToMissing(value: unknown): unknown {
  return value === '' ? undefined : value;
}

/** Map a blank string (empty or whitespace only) to `undefined`. */
function blankToMissing(value: unknown): unknown {
  return typeof value === 'string' && value.trim() === '' ? undefined : value;
}

/**
 * A field's value with "no value" mapped to `undefined`, by the rule
 * `buildFieldSchema` validates with: `undefined`, `null` and `''` -- plus a
 * whitespace-only string in a `number` field -- are all missing. Any other
 * value is returned as-is. A key that is not a field of the form (no
 * `fieldConfig`) is returned as-is.
 *
 * Cross-field validators receive the form values in this form, so they only
 * have one missing value to handle.
 */
export function normalizeMissingValue(
  fieldConfig: FieldConfig | undefined,
  value: unknown,
): unknown {
  if (fieldConfig === undefined) {
    return value;
  }
  if (isMissing(value)) {
    return undefined;
  }
  return fieldConfig.type === 'number' ? blankToMissing(value) : emptyToMissing(value);
}

/**
 * Fold a field's evaluated required state (base config + `conditions` + the
 * rule overlay, as computed by `useFieldConditions`) into its validation
 * config, so a field made required at runtime is validated as required --
 * with the default message -- and one a condition relaxes is not. When there
 * is no evaluated state, or it matches the config, the config is returned
 * untouched (a custom required message is kept).
 */
function withRequiredState(
  fieldConfig: FieldConfig,
  required: boolean | undefined,
): FieldConfig {
  if (required === undefined || required === isRequired(fieldConfig.validation)) {
    return fieldConfig;
  }
  return { ...fieldConfig, validation: { ...fieldConfig.validation, required } };
}

/**
 * Build a complete Zod object schema from the field configurations.
 *
 * Only fields that are currently visible (according to `visibilityMap`)
 * are included. Hidden fields are omitted so they don't trigger
 * validation errors.
 *
 * @param fields        - All field configurations keyed by field name.
 * @param visibilityMap - Map of field name to visibility boolean.
 * @param requiredMap   - Map of field name to its current required state
 *                        (conditions included). A field absent from the map
 *                        keeps its config's `validation.required`.
 * @returns A Zod object schema covering all visible fields.
 */
export function buildFormSchema(
  fields: Record<string, FieldConfig>,
  visibilityMap: Record<string, boolean>,
  requiredMap: Record<string, boolean> = {},
): z.ZodObject<Record<string, z.ZodType>> {
  const shape: Record<string, z.ZodType> = {};

  for (const [fieldName, fieldConfig] of Object.entries(fields)) {
    // Skip hidden fields
    if (visibilityMap[fieldName] === false) {
      continue;
    }

    // Only add visible fields; if not in the map assume visible.
    if (fieldConfig) {
      shape[fieldName] = buildFieldSchema(
        withRequiredState(fieldConfig, requiredMap[fieldName]),
      );
    }
  }

  return z.object(shape);
}
